"use client";

import React from "react";
import { motion } from "framer-motion";
import { Loader as Loader2, CircleCheck as CheckCircle2 } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
function MaintenanceForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-lg border border-green-200 bg-green-50 p-6">
                <p className="text-green-800">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-4">
                    Submit Another Request
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
            <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>Full Name</FormLabel>
                        <FormControl>
                            <Input placeholder="John Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />
            <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^\S+@\S+$/i,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                            <Input type="email" placeholder="john@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />
            <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>Phone Number</FormLabel>
                        <FormControl>
                            <Input type="tel" placeholder="01234 567890" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />
            <FormField control={control} name="message" rules={{
      required: "Please provide details about your requirement"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>How can we help?</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Describe your maintenance needs or any issues you are experiencing..." className="min-h-[120px] resize-y" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                    {submitError}
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base font-medium mt-2">
                {isSubmitting ? <>
                        <Loader2 className="mr-2 size-5 animate-spin" />
                        Sending Request...
                    </> : "Request Maintenance"}
            </Button>
        </Form>;
}
export default function MaintenanceServices() {
  return <section id="maintenanceservices" className="bg-background py-24">
            <div className="mx-auto max-w-4xl px-6">
                <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="mb-12 text-center">
                    <h2 className="mb-4 font-default text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                        Keep Your System Running
                    </h2>
                    <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
                        Regular maintenance ensures your air conditioning system operates at peak efficiency, reducing energy costs and preventing unexpected breakdowns.
                    </p>
                </motion.div>

                <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6,
        delay: 0.2,
        ease: "easeOut"
      }}>
                    <Card className="rounded-xl border border-border bg-card p-8 shadow-md md:p-10">
                        <CardHeader className="px-0 pt-0 pb-6">
                            <CardTitle className="text-2xl font-default text-foreground">Request Maintenance</CardTitle>
                            <CardDescription className="text-muted-foreground text-base mt-2">
                                Fill out the form below and our team will get back to you shortly to schedule a visit.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="px-0 pb-0">
                            <FormProvider formId="contact_inquiry_form" sectionName="maintenance-services" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: ""
            }} submitText="Request Maintenance" successMessage="Thank you! Your maintenance request has been received. We will contact you shortly.">
                                <MaintenanceForm />
                            </FormProvider>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </section>;
}